import React, { useState, useEffect, MouseEventHandler } from 'react';
import { get, isEmpty, isNil, orderBy } from 'lodash';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment-timezone';
import { FormControl, MenuItem, Select } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import frLocale from 'date-fns/locale/fr';
import { useSelector } from 'react-redux';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import {
  Container,
  StyledInputBase,
  SearchButton,
  RefreshButton,
  StyledKeyboardDatePicker,
} from 'components/NewSearchBar/style';
import { getDateIgnoreTimezone } from 'containers/common/LegalProceedings/utils';
import { injectIntl, IntlShape } from 'react-intl';
import messages from 'components/NewSearchBar/messages';
import { translate } from 'containers/services/translate';
import { COLORS } from 'constants/design';
import CalendarIcon from 'components/SahamIcons/CalendarIcon';
import {
  SEARCH_BAR_TYPES,
  SEARCH_TYPE_IDS,
} from 'components/NewSearchBar/constants';
import { DATE_FORMAT } from 'constants/global';
import userPrivileges, {
  PRIVILEGES,
} from 'containers/services/security/user-privileges';
import { FEATURES } from 'features/features';
import * as featureService from 'features/fflip-helpers';
import { CORRECTIONAL_JUDICIARY_PROCESS_ID } from 'containers/LawyerFeeNote/constants';

export interface SearchQuery {
  lossDate: string | null;
  registrationNumber: string | null;
  policyNumber: string | null;
  claimNumber: string | null;
  assuredCIN: string | null;
  assuredName: string | null;
  victimName: string | null;
  courtNumber: string | null;
  procedureNatureId: number | null;
  jurisdictionDegreeId: number | null;
  jurisdiction: string | null;
}
export interface NewSearchBarProps {
  intl: IntlShape;
  searchQuery: any;
  setSearchQuery: Function;
  handlerSearch: () => void;
  handlerRefresh: MouseEventHandler;
  isHomePage: boolean;
  showAll?: boolean;
  customCriterias?: any;
  isManager?: boolean;
}

const NewSearchBar: React.FC<NewSearchBarProps> = (props): JSX.Element => {
  const { handlerSearch, handlerRefresh, searchQuery, setSearchQuery } = props;
  const [searchType, setSearchType] = useState([] as any);

  const procedures: any = useSelector((state: any) =>
    get(state.toJS(), ['appContainer', 'listings', 'binJudiciaryProcess']),
  );
  const juridictionDegreeListing = useSelector((state: any) =>
    get(state.toJS(), ['appContainer', 'listings', 'jurisdictionDegrees']),
  );
  const juridictionListing = useSelector((state: any) =>
    get(state.toJS(), ['appContainer', 'listings', 'jurisdiction']),
  );
  if (procedures) {
    procedures.unshift({
      id: 0,
      title: translate(messages.procedureType, props),
    });
  }
  if (juridictionDegreeListing) {
    juridictionDegreeListing.unshift({
      id: 0,
      title: translate(messages.juridictionDegree, props),
    });
  }
  if (juridictionListing) {
    juridictionListing.unshift({
      id: 0,
      title: translate(messages.juridiction, props),
    });
  }
  const [procedureState, setProcedureState] = useState<number>(0);
  const handleChangeProcedure = (e: React.ChangeEvent<{ value: unknown }>) => {
    setProcedureState(
      get(
        procedures.find((pro: any) => pro.id === e.target.value),
        'id',
      ),
    );
  };

  const [selectedSearchType, setSelectedSearchType] = useState(
    props.showAll ? SEARCH_TYPE_IDS.CLAIM : SEARCH_TYPE_IDS.REGISTER,
  );
  const handleChangeSelectedSearchType = (
    e: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setSelectedSearchType(e.target.value as string);
  };

  const [searchNameValue, setSearchNameValue] = React.useState<string>('');

  const handleChangeNameValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchNameValue(e.target.value.trim());
  };

  const [searchValue, setSearchValue] = useState<string | null>(null);

  const handleChangeSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value.trim());
  };
  const [searchValuePlaceHolder, setSearchValuePlaceHolder] = useState('');

  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);

  const [
    searchJuridictionDegree,
    setSearchJuridictionDegree,
  ] = React.useState<Number | null>(0);
  const handleChangeJuridictionDegreeValue = (
    e: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setSearchJuridictionDegree(
      get(
        juridictionDegreeListing.find((pro: any) => pro.id === e.target.value),
        'id',
      ),
    );
  };
  const [
    searchJuridiction,
    setSearchJuridiction,
  ] = React.useState<Number | null>(0);
  /* istanbul ignore next */
  const handleChangeJuridictionValue = (
    e: React.ChangeEvent<{ value: unknown }>,
  ) => {
    setSearchJuridiction(
      get(
        juridictionListing.find((pro: any) => pro.id === e.target.value),
        'id',
      ),
    );
  };
  const [preOpenDate, setPreOpenDate] = React.useState<Date | null>(null);
  const handleDateChange = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    setSelectedDate(theDate);
  };
  const handlePreOpenDateChange = (date: Date | null) => {
    const theDate = getDateIgnoreTimezone(date);
    setPreOpenDate(theDate);
  };

  useEffect(() => {
    props.showAll
      ? setSearchType(allSearchTypes[SEARCH_BAR_TYPES.DEFAULT])
      : setSearchType(allSearchTypes[SEARCH_BAR_TYPES.SHOW_OTHER]); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.showAll]);

  const searchBarTypesBase = [
    {
      id: SEARCH_TYPE_IDS.ASSURED_NAME,
      title: translate(messages.assuredName, props),
    },
    {
      id: SEARCH_TYPE_IDS.VICTIM_NAME,
      title: translate(messages.victimName, props),
    },
    {
      id: SEARCH_TYPE_IDS.COURT_FILE_NUMBER,
      title: translate(messages.courtFileNumber, props),
    },
    {
      id: SEARCH_TYPE_IDS.REGISTER,
      title: translate(messages.register, props),
    },
    {
      id: SEARCH_TYPE_IDS.POLICY,
      title: translate(messages.policy, props),
    },
    {
      id: SEARCH_TYPE_IDS.ASSURED_CIN,
      title: translate(messages.assuredCIN, props),
    },
    {
      id: SEARCH_TYPE_IDS.CLAIM,
      title: translate(messages.claim, props),
    },
  ];

  const searchBarVcTypesBase = [
    {
      id: SEARCH_TYPE_IDS.CIN_ASSURE,
      title: translate(messages.cin_assure, props),
    },
    {
      id: SEARCH_TYPE_IDS.FULL_NAME_ASSURE,
      title: translate(messages.full_name_assure, props),
    },
    {
      id: SEARCH_TYPE_IDS.REF_ATTESTATION,
      title: translate(messages.ref_attestation, props),
    },
  ];

  const defaultsearchType = [
    {
      id: SEARCH_TYPE_IDS.REGISTER,
      title: translate(messages.register, props),
    },
    {
      id: SEARCH_TYPE_IDS.POLICY,
      title: translate(messages.policy, props),
    },
    {
      id: SEARCH_TYPE_IDS.ASSURED_CIN,
      title: translate(messages.assuredCIN, props),
    },
  ];

  const allSearchTypes = {
    [SEARCH_BAR_TYPES.DEFAULT]:
      userPrivileges.userHasPrivilege(PRIVILEGES.ISOPENINGMANAGER) &&
      userPrivileges.userHasPrivilege(PRIVILEGES.ISMANAGER) &&
      featureService.hasFeature(FEATURES.ASSIGNMENT_JURISDICTION_LAWYER)
        ? [
            ...searchBarTypesBase,
            {
              id: SEARCH_TYPE_IDS.DEFAULT_LAWYER_ASSIGNMENT,
              title: translate(messages.defaultLawyerAssignment, props),
            },
            ...(featureService.hasFeature(FEATURES.BASIC_MANAGEMENT)
              ? searchBarVcTypesBase
              : []),
          ]
        : [
            ...searchBarTypesBase,
            ...(featureService.hasFeature(FEATURES.BASIC_MANAGEMENT)
              ? searchBarVcTypesBase
              : []),
          ],
    [SEARCH_BAR_TYPES.SHOW_OTHER]: [
      ...(props.customCriterias
        ? [...props.customCriterias]
        : featureService.hasFeature(FEATURES.BASIC_MANAGEMENT) ||
          (featureService.hasFeature(FEATURES.BASIC_MANAGEMENT_REGULATION) &&
            userPrivileges.userHasPrivilege(PRIVILEGES.ISCOURTMANAGER) &&
            !props.isManager)
        ? [
            ...defaultsearchType,
            {
              id: SEARCH_TYPE_IDS.CLAIM_NUMBER,
              title: translate(messages.claimNumber, props),
            },
          ]
        : [...defaultsearchType]),
    ],
  };
  useEffect(() => {
    const result = searchType.find(
      (type: { id: string }) => type.id === selectedSearchType,
    );
    if (result) {
      if (result.id === SEARCH_TYPE_IDS.COURT_FILE_NUMBER) {
        setSearchValuePlaceHolder(`NNNNNN/NNNN/NNNN`);
      } else if (result.id === SEARCH_TYPE_IDS.FULL_NAME_ASSURE) {
        /* istanbul ignore next */
        setSearchValuePlaceHolder(`Saisir Prenom`);
      } else {
        setSearchValuePlaceHolder(`Saisir ${result.title}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSearchType, !isEmpty(searchType)]);
  const assignValuesToSearchQueryBeforeSearch = () => {
    searchQuery.lossDate =
      selectedDate && moment(selectedDate).format(DATE_FORMAT);

    searchQuery.preOpenDate =
      preOpenDate && moment(preOpenDate).format(DATE_FORMAT);

    searchQuery.registrationNumber = null;
    searchQuery.policyNumber = null;
    searchQuery.assuredCIN = null;
    searchQuery.assuredName = null;
    searchQuery.victimName = null;
    searchQuery.courtNumber = null;
    searchQuery.claimNumber = null;
    searchQuery.procedureNatureId = null;
    searchQuery.jurisdictionDegreeId = null;
    searchQuery.jurisdiction = null;
    searchQuery.isReserveLawyer = false;
    searchQuery.claimNumber = null;
    searchQuery.cinAssure = null;
    searchQuery.firstName = null;
    searchQuery.lastName = null;
    searchQuery.refAttestation = null;

    switch (selectedSearchType) {
      case SEARCH_TYPE_IDS.CLAIM:
        searchQuery.claimNumber = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      case SEARCH_TYPE_IDS.DEFAULT_LAWYER_ASSIGNMENT:
        searchQuery.procedureNatureId = CORRECTIONAL_JUDICIARY_PROCESS_ID;
        searchQuery.isReserveLawyer = true;
        break;
      case SEARCH_TYPE_IDS.REGISTER:
        searchQuery.registrationNumber = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      case SEARCH_TYPE_IDS.CLAIM_NUMBER:
        searchQuery.claimNumber = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      case SEARCH_TYPE_IDS.POLICY:
        searchQuery.policyNumber = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      case SEARCH_TYPE_IDS.ASSURED_CIN:
        searchQuery.assuredCIN = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      case SEARCH_TYPE_IDS.ASSURED_NAME:
        searchQuery.assuredName = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      case SEARCH_TYPE_IDS.VICTIM_NAME:
        searchQuery.victimName = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      case SEARCH_TYPE_IDS.COURT_FILE_NUMBER:
        searchQuery.courtNumber = searchValue;
        searchQuery.procedureNatureId = procedureState;
        searchQuery.jurisdictionDegreeId = searchJuridictionDegree;
        searchQuery.jurisdiction = String(searchJuridiction);
        break;
      /* istanbul ignore next */
      case SEARCH_TYPE_IDS.CIN_ASSURE:
        searchQuery.cinAssure = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      /* istanbul ignore next */
      case SEARCH_TYPE_IDS.FULL_NAME_ASSURE:
        searchQuery.firstName = searchValue;
        searchQuery.lastName = searchNameValue;
        searchQuery.procedureNatureId = procedureState;
        break;
      /* istanbul ignore next */
      case SEARCH_TYPE_IDS.REF_ATTESTATION:
        searchQuery.refAttestation = searchValue;
        searchQuery.procedureNatureId = procedureState;
        break;
    }
    setSearchQuery(searchQuery);
    handlerSearch();
  };
  const handleRefresh = () => {
    setSearchValue('');
    setSelectedSearchType(SEARCH_TYPE_IDS.REGISTER);
    //@ts-ignore
    handlerRefresh();
  };
  return (
    <Container>
      {!props.isHomePage && (
        <FormControl
          className="select-procedure-type"
          error={!Boolean(procedureState)}
        >
          <Select
            onChange={handleChangeProcedure}
            input={
              <StyledInputBase marginRight fullWidth textColor="black" bold />
            }
            value={procedureState}
            defaultValue={0}
          >
            {procedures &&
              procedures.map((procedure: any) => (
                <MenuItem value={procedure.id}>{procedure.title}</MenuItem>
              ))}
          </Select>
        </FormControl>
      )}
      <Select
        value={selectedSearchType}
        onChange={handleChangeSelectedSearchType}
        input={
          <StyledInputBase borderRadiusLeft fullWidth textColor="black" bold />
        }
        data-testid="search-type"
      >
        {searchType.map((searchType: any) => (
          <MenuItem value={searchType.id}>{searchType.title}</MenuItem>
        ))}
      </Select>
      {selectedSearchType !== SEARCH_TYPE_IDS.DEFAULT_LAWYER_ASSIGNMENT && (
        <>
          <FormControl style={{ height: '40px', width: 'min-content' }}>
            <StyledInputBase
              marginRight
              borderRadiusRight
              fullWidth
              textColor="black"
              bgColor={COLORS.FORM_INPUT_BG_GRAY}
              className="inputBase"
              placeholder={searchValuePlaceHolder}
              name="queryNumber"
              onKeyPress={
                /* istanbul ignore next */ e => {
                  if (
                    e.key === 'Enter' &&
                    searchValue != '' &&
                    !isNil(searchValue)
                  ) {
                    assignValuesToSearchQueryBeforeSearch();
                  }
                }
              }
              value={searchValue}
              onChange={handleChangeSearchValue}
              inputProps={{
                'aria-label': 'searchedValue',
                className: 'inputTextStyle',
              }}
            />
          </FormControl>
          {selectedSearchType === SEARCH_TYPE_IDS.FULL_NAME_ASSURE && (
            <FormControl style={{ height: '40px', width: 'min-content' }}>
              <StyledInputBase
                marginRight
                borderRadiusRight
                fullWidth
                textColor="black"
                bgColor={COLORS.FORM_INPUT_BG_GRAY}
                className="inputBase"
                placeholder={'Saisir Nom'}
                name="queryNumber"
                value={searchNameValue}
                onChange={handleChangeNameValue}
                inputProps={{
                  'aria-label': 'searchedValue',
                  className: 'inputTextStyle',
                }}
              />
            </FormControl>
          )}
        </>
      )}
      {props.isHomePage &&
      selectedSearchType === SEARCH_TYPE_IDS.COURT_FILE_NUMBER ? (
        <div
          style={{
            paddingLeft: 10,
            paddingRight: 10,
          }}
        >
          <Select
            onChange={handleChangeProcedure}
            input={
              <StyledInputBase marginRight fullWidth textColor="black" bold />
            }
            value={procedureState}
            defaultValue={0}
          >
            {procedures &&
              procedures.map(
                /* istanbul ignore next */ (procedure: any) => (
                  <MenuItem value={procedure.id}>{procedure.title}</MenuItem>
                ),
              )}
          </Select>
          <Select
            onChange={handleChangeJuridictionDegreeValue}
            input={
              <StyledInputBase marginRight fullWidth textColor="black" bold />
            }
            value={searchJuridictionDegree}
            defaultValue={0}
          >
            {juridictionDegreeListing &&
              juridictionDegreeListing.map(
                /* istanbul ignore next */ (procedure: any) => (
                  <MenuItem value={procedure.id}>{procedure.title}</MenuItem>
                ),
              )}
          </Select>
          <Select
            onChange={handleChangeJuridictionValue}
            input={
              <StyledInputBase marginRight fullWidth textColor="black" bold />
            }
            value={searchJuridiction}
            defaultValue={0}
          >
            {juridictionListing &&
              orderBy(
                juridictionListing,
                [/* istanbul ignore next */ el => el.title.toLowerCase()],
                ['asc'],
              ).map(
                /* istanbul ignore next */ (procedure: any) => (
                  <MenuItem value={procedure.id}>{procedure.title}</MenuItem>
                ),
              )}
          </Select>
        </div>
      ) : (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
          <StyledKeyboardDatePicker
            autoOk
            disableFuture
            variant="inline"
            invalidDateMessage={
              moment(selectedDate).format(DATE_FORMAT) === 'Invalid date' &&
              translate({ ...messages.invalidDate }, props)
            }
            inputVariant="standard"
            format="dd/MM/yyyy"
            InputAdornmentProps={{
              position: 'end',
            }}
            value={selectedDate}
            onChange={handleDateChange}
            placeholder={translate({ ...messages.lossDate }, props)}
            keyboardIcon={<CalendarIcon />}
            InputProps={{
              disableUnderline: true,
              className: 'inputTextStyleDate',
            }}
            onKeyPress={
              /* istanbul ignore next */ e => {
                if (
                  e.key === 'Enter' &&
                  !isNil(selectedDate) &&
                  moment(selectedDate).format(DATE_FORMAT) != 'Invalid date'
                ) {
                  assignValuesToSearchQueryBeforeSearch();
                }
              }
            }
            error={
              !isNil(selectedDate) &&
              moment(selectedDate).format(DATE_FORMAT) === 'Invalid date'
            }
            helperText={
              !isNil(selectedDate) &&
              moment(selectedDate).format(DATE_FORMAT) === 'Invalid date' &&
              translate({ ...messages.invalidDate }, props)
            }
          />
        </MuiPickersUtilsProvider>
      )}
      {props.customCriterias && (
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frLocale}>
          <StyledKeyboardDatePicker
            autoOk
            disableFuture
            variant="inline"
            invalidDateMessage=""
            inputVariant="standard"
            format="dd/MM/yyyy"
            InputAdornmentProps={{
              position: 'end',
            }}
            value={preOpenDate}
            onChange={handlePreOpenDateChange}
            placeholder={translate({ ...messages.preOpenDate }, props)}
            keyboardIcon={<CalendarIcon />}
            InputProps={{
              disableUnderline: true,
              className: 'inputTextStyleDate',
            }}
            error={
              !isNil(preOpenDate) &&
              moment(preOpenDate).format(DATE_FORMAT) === 'Invalid date'
            }
            helperText={
              !isNil(preOpenDate) &&
              moment(preOpenDate).format(DATE_FORMAT) === 'Invalid date' &&
              translate({ ...messages.invalidDate }, props)
            }
          />
        </MuiPickersUtilsProvider>
      )}
      <SearchButton
        data-testid="search-button"
        onClick={assignValuesToSearchQueryBeforeSearch}
        disabled={
          (!isNil(selectedDate) &&
            moment(selectedDate).format(DATE_FORMAT) === 'Invalid date') ||
          (!isNil(preOpenDate) &&
            moment(preOpenDate).format(DATE_FORMAT) === 'Invalid date') ||
          (selectedSearchType === SEARCH_TYPE_IDS.CLAIM &&
            (isNil(searchValue) || searchValue === '')) ||
          (selectedSearchType === SEARCH_TYPE_IDS.POLICY &&
            (isNil(searchValue) || searchValue === '')) ||
          (selectedSearchType === SEARCH_TYPE_IDS.REGISTER &&
            (isNil(searchValue) || searchValue === ''))
        }
      >
        <SearchIcon />
      </SearchButton>
      <RefreshButton data-testid="refresh-button" onClick={handleRefresh}>
        <RefreshIcon />
      </RefreshButton>
    </Container>
  );
};

export default injectIntl(NewSearchBar);
